import { Link } from 'react-router-dom';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

export const Privacy = () => {
  return (
    <div data-testid="privacy-page">
      {/* Hero */}
      <section className="relative py-24 bg-navy" data-testid="privacy-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Legal
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-navy-200">Last updated: December 2024</p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-navy max-w-none">
            <div className="space-y-8 text-navy-700 leading-relaxed">
              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">1. Introduction</h2>
                <p>
                  HealthCheck Medical Inc. ("we," "our," or "us") is committed to protecting the privacy and security of personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website healthcentermedicalgroup.online or use our services.
                </p>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">2. Information We Collect</h2>
                <p className="mb-3">We may collect the following types of information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Personal Information:</strong> Name, email address, phone number, organization name, and job title</li>
                  <li><strong>Health Information:</strong> Protected Health Information (PHI) governed by HIPAA regulations</li>
                  <li><strong>Business Information:</strong> Company details, practice size, and service requirements</li>
                  <li><strong>Technical Information:</strong> IP address, browser type, device information, and usage data</li>
                </ul>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">3. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide, maintain, and improve our management services</li>
                  <li>Process revenue cycle management and administrative tasks</li>
                  <li>Communicate with you about services, updates, and support</li>
                  <li>Comply with legal obligations and HIPAA requirements</li>
                  <li>Analyze service usage to enhance customer experience</li>
                </ul>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">4. HIPAA Compliance</h2>
                <p>
                  As a healthcare management services organization, HealthCheck Medical Inc. is committed to full compliance with the Health Insurance Portability and Accountability Act (HIPAA). All Protected Health Information (PHI) is handled in accordance with HIPAA Privacy and Security Rules. We maintain Business Associate Agreements (BAAs) with all our healthcare partners.
                </p>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">5. Data Security</h2>
                <p>
                  We implement industry-standard security measures including encryption, access controls, regular security audits, and employee training to protect your information from unauthorized access, alteration, or disclosure.
                </p>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">6. Information Sharing</h2>
                <p>
                  We do not sell your personal information. We may share information with trusted service providers, healthcare partners under BAA agreements, or when required by law.
                </p>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">7. Your Rights</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access and review your personal information</li>
                  <li>Request corrections to inaccurate data</li>
                  <li>Request deletion of your information (subject to legal requirements)</li>
                  <li>Opt-out of marketing communications</li>
                </ul>
              </div>

              <div>
                <h2 className="font-manrope font-bold text-2xl text-navy mb-4">8. Contact Us</h2>
                <p className="mb-4">If you have questions about this Privacy Policy, please contact us:</p>
                <div className="bg-navy-50 p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-emerald" />
                    <span className="font-manrope font-semibold text-navy">HealthCheck Medical Inc.</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-emerald" />
                    <span className="text-sm">7100 Commerce Way Ste 182, Brentwood, TN 37027</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-emerald" />
                    <span className="text-sm">(855) 831-1992</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-emerald" />
                    <span className="text-sm">privacy@healthcentermedicalgroup.online</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-silver-light">
            <Button 
              asChild
              className="bg-navy hover:bg-navy-700 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
              data-testid="privacy-contact-btn"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
